// AccountMenu — the avatar (account) panel. "Me" things only: identity, profile,
// the personal preferences that live here (theme + language), password, sign
// out. The gear (project), switcher (context), and notifications (work) stay OUT
// — different scope.
//
// It is a POPOVER, not a Menu (COMPONENTS §Overlay patterns): a small card of
// MIXED content — an identity header, a segmented Appearance control, a Language
// radio group, navigation rows, and a destructive Sign out — that you Tab
// between, not a uniform list you arrow through. React Aria's Popover owns the
// positioning / focus / Escape / click-outside / RTL; we own the contents. The
// preference controls are native-owned (native radios → roving + selection free).
//
// `minimal` variant (the SlimAuthedLayout dead-end — /no-projects): omits My
// Profile + Change Password, keeping identity + the always-safe personal actions
// (theme, language, sign out).
//
// Theme + language are self-mutating PATCHes via useProfilePrefs (invalidate
// ['me'] + Layer-3 broadcast).

import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { icons } from '../ui/icons.js';
import { useMe } from '../../hooks/useMe.js';
import { usePerms } from '../../hooks/usePerms.js';
import { useProfilePrefs } from '../../hooks/useProfilePrefs.js';
import { useLogout } from '../../hooks/useLogout.js';
import { useToast } from '../ui/Toast.js';
import { IconButton } from '../ui/IconButton.js';
import { Popover } from '../ui/Popover.js';
import { ThemeModeControl } from './ThemeModeControl.js';

// Shared row affordance for the panel's links + actions (the account-local
// equivalent of the old MenuLink/MenuItem look — full-width, hover/focus tint).
const rowClass =
  'w-full flex items-center gap-2.5 px-4 py-2 text-sm pointer-coarse:min-h-12 pointer-coarse:text-base text-start ' +
  'hover:bg-[var(--color-bg-hover)] focus-visible:bg-[var(--color-bg-hover)] outline-none';

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-4 pt-2 pb-0.5 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
      {children}
    </div>
  );
}

function Divider() {
  return <div aria-hidden className="my-1 border-t border-[var(--color-divider)]" />;
}

const LANGUAGES = ['en', 'fr', 'ar'] as const;

export function AccountMenu({
  projectId,
  minimal = false,
}: {
  projectId: string | null;
  minimal?: boolean;
}) {
  const { t } = useTranslation(['nav', 'common']);
  const { data: me } = useMe();
  const { data: perms } = usePerms(projectId ?? '');
  const prefs = useProfilePrefs();
  const logout = useLogout();
  const toast = useToast();
  const langName = useId();

  if (!me) return null;

  const fullName = `${me.user.first_name} ${me.user.last_name}`.trim();
  const roleBadge = me.user.is_sysadmin
    ? t('account.sysadminBadge')
    : projectId && perms?.is_project_admin
      ? t('account.projectAdminBadge')
      : null;

  return (
    <Popover
      label={t('account.menuLabel')}
      placement="bottom end"
      renderTrigger={() => (
        <IconButton aria-label={t('account.menuLabel')} className="rounded-full">
          <icons.user aria-hidden size={20} />
        </IconButton>
      )}
    >
      {(close) => (
        <div className="py-1">
          {/* Identity header */}
          <div className="px-4 py-3 border-b border-[var(--color-divider)]">
            <div className="font-medium text-[var(--color-text-primary)] truncate">{fullName}</div>
            <div className="text-xs text-[var(--color-text-secondary)] truncate">{me.user.username}</div>
            {/* Identity descriptor, not a status — plain muted text per
                COMPONENTS reuse rules (a Badge labels a mutable state). */}
            {roleBadge && (
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)] truncate">{roleBadge}</div>
            )}
          </div>

          {!minimal && (
            <Link to="/profile" onClick={close} className={`${rowClass} text-[var(--color-text-primary)]`}>
              <icons.user aria-hidden size={18} />
              <span className="flex-1 truncate">{t('account.myProfile')}</span>
            </Link>
          )}

          <Divider />

          {/* Appearance — a segmented native-radio picker; `system` follows the OS. */}
          <GroupLabel>{t('account.appearance')}</GroupLabel>
          <ThemeModeControl
            value={prefs.themeMode ?? 'system'}
            onSelect={prefs.setThemeMode}
            groupLabel={t('account.appearance')}
          />

          {/* Language — native-radio rows (roving + single-selection free). */}
          <GroupLabel>{t('account.language')}</GroupLabel>
          <fieldset className="m-0 border-0 p-0">
            <legend className="sr-only">{t('account.language')}</legend>
            {LANGUAGES.map((code) => {
              const active = prefs.language === code;
              return (
                <label
                  key={code}
                  className={`${rowClass} cursor-pointer justify-between text-[var(--color-text-primary)] has-[:focus-visible]:bg-[var(--color-bg-hover)]`}
                >
                  <span className="flex items-center gap-2.5">
                    <input
                      type="radio"
                      name={langName}
                      className="sr-only"
                      checked={active}
                      onChange={() => prefs.setLanguage(code)}
                    />
                    {t(`account.languageNames.${code}`)}
                  </span>
                  {active && (
                    <icons.check aria-hidden size={16} className="text-[var(--color-primary-display)]" />
                  )}
                </label>
              );
            })}
          </fieldset>

          <Divider />

          {!minimal && (
            <>
              <Link
                to="/change-password"
                onClick={close}
                className={`${rowClass} text-[var(--color-text-primary)]`}
              >
                <icons.password aria-hidden size={18} />
                <span className="flex-1 truncate">{t('account.changePassword')}</span>
              </Link>
              {/* Enable-only nudge — placeholder toast until the enrollment slice
                  (APP-143) lands the real flow + `mfa_enabled` on /me. */}
              <button
                type="button"
                className={`${rowClass} text-[var(--color-text-primary)]`}
                onClick={() => {
                  close();
                  toast.info(t('tools.comingSoon'));
                }}
              >
                <icons.role aria-hidden size={18} />
                <span className="flex-1 truncate">{t('account.enableMfa')}</span>
                {/* A nudge, not a status — muted caption per COMPONENTS reuse rules. */}
                <span className="text-xs text-[var(--color-text-secondary)]">{t('account.recommended')}</span>
              </button>
              <Divider />
            </>
          )}

          <button
            type="button"
            className={`${rowClass} text-[var(--color-danger)]`}
            onClick={() => {
              close();
              logout.mutate();
            }}
          >
            <icons.signOut aria-hidden size={18} />
            <span className="flex-1 truncate">{t('account.signOut')}</span>
          </button>
        </div>
      )}
    </Popover>
  );
}
